import React, { useContext } from "react";
import {
  Box,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Tooltip,
  Typography,
} from "@mui/material";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import RoomServiceIcon from "@mui/icons-material/RoomService";
import AllInboxIcon from "@mui/icons-material/AllInbox";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import HouseIcon from "@mui/icons-material/House";
import ReceiptIcon from "@mui/icons-material/Receipt";
import BlockIcon from "@mui/icons-material/Block";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import AccessAlarmIcon from "@mui/icons-material/AccessAlarm";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import RateReviewIcon from "@mui/icons-material/RateReview";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import SyncIcon from "@mui/icons-material/Sync";
import DescriptionIcon from "@mui/icons-material/Description";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import AssignmentIcon from "@mui/icons-material/Assignment";
import SettingsIcon from "@mui/icons-material/Settings";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import SearchIcon from "@mui/icons-material/Search";
import { AuthContext } from "../../utils/userProvider";

function hasTab(authState, key) {
  return authState?.tabAccess && authState.tabAccess.includes(key);
}

function NavItem({ open, selected, icon, label, onClick }) {
  const button = (
    <ListItemButton
      selected={selected}
      onClick={onClick}
      sx={{
        mx: open ? 1 : 0.5,
        mb: 0.25,
        borderRadius: 2,
        minHeight: 42,
        justifyContent: open ? "initial" : "center",
        px: open ? 1.5 : 1,
        "&.Mui-selected": {
          backgroundColor: "rgba(0, 186, 178, 0.12)",
          color: "primary.main",
          "& .MuiListItemIcon-root": { color: "primary.main" },
          "&:hover": { backgroundColor: "rgba(0, 186, 178, 0.18)" },
        },
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: 0,
          mr: open ? 1.5 : 0,
          justifyContent: "center",
          color: "text.secondary",
        }}
      >
        {icon}
      </ListItemIcon>
      {open && (
        <ListItemText
          primary={label}
          primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
        />
      )}
    </ListItemButton>
  );

  if (open) return button;
  return (
    <Tooltip title={label} placement="right">
      {button}
    </Tooltip>
  );
}

function SectionHeader({ open, label }) {
  if (!open) {
    return <Divider sx={{ my: 1, mx: 1 }} />;
  }
  return (
    <ListSubheader
      disableSticky
      sx={{
        bgcolor: "transparent",
        lineHeight: "28px",
        mt: 1,
        mb: 0.25,
        px: 2.5,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "text.secondary",
      }}
    >
      {label}
    </ListSubheader>
  );
}

export default function ListItems(props) {
  const { authState } = useContext(AuthContext);
  const path = (props.path || "").toLowerCase();
  const open = props.open !== false;

  const inventoryItems = [
    hasTab(authState, "city") && {
      key: "city",
      label: "City",
      icon: <LocationCityIcon fontSize="small" />,
      selected: path.includes("/city"),
      onClick: props.city,
    },
    hasTab(authState, "property") && {
      key: "property",
      label: "Properties",
      icon: <HouseIcon fontSize="small" />,
      selected: path.includes("/property"),
      onClick: props.properties,
    },
    hasTab(authState, "roomtypes") && {
      key: "roomtypes",
      label: "Room Types",
      icon: <RoomServiceIcon fontSize="small" />,
      selected: path.includes("roomtypes"),
      onClick: props.roomTypes,
    },
    hasTab(authState, "addons") && {
      key: "addons",
      label: "Add-ons",
      icon: <AllInboxIcon fontSize="small" />,
      selected: path.includes("addons"),
      onClick: props.addons,
    },
  ].filter(Boolean);

  const commerceItems = [
    hasTab(authState, "transactions") && {
      key: "transactions",
      label: "Active Transactions",
      icon: <ReceiptIcon fontSize="small" />,
      selected: path.includes("transactions") && !path.includes("expired"),
      onClick: props.transactions,
    },
    hasTab(authState, "expiredTransactions") && {
      key: "expiredTransactions",
      label: "Expired Transactions",
      icon: <BlockIcon fontSize="small" />,
      selected: path.includes("expiredtransactions"),
      onClick: props.expiredTransactions,
    },
    hasTab(authState, "promotions") && {
      key: "promotions",
      label: "Promotions",
      icon: <MonetizationOnIcon fontSize="small" />,
      selected: path.includes("promotions") || path.includes("promotion-rules"),
      onClick: props.promotions,
    },
    hasTab(authState, "rate-numbers") && {
      key: "rate-numbers",
      label: "Rate Numbers",
      icon: <AttachMoneyOutlinedIcon fontSize="small" />,
      selected: path.includes("rate-numbers"),
      onClick: props.rateNumbers,
    },
    {
      key: "referralCodes",
      label: "Referral Codes",
      icon: <MonetizationOnIcon fontSize="small" />,
      selected: path.includes("referralcodes"),
      onClick: props.referralCodes,
    },
    hasTab(authState, "gifts") && {
      key: "gifts",
      label: "Gifts",
      icon: <CardGiftcardIcon fontSize="small" />,
      selected: path.includes("gift"),
      onClick: props.gifts,
    },
    hasTab(authState, "creditnote") && {
      key: "creditnote",
      label: "Credit Notes",
      icon: <AccessAlarmIcon fontSize="small" />,
      selected: path.includes("creditnote"),
      onClick: props.creditNote,
    },
    hasTab(authState, "stripefees") && {
      key: "stripefees",
      label: "Stripe Fees",
      icon: <CreditCardIcon fontSize="small" />,
      selected: path.includes("stripefees"),
      onClick: props.stripeFees,
    },
  ].filter(Boolean);

  const operationsItems = [
    {
      key: "searchAttempts",
      label: "Search Attempts",
      icon: <SearchIcon fontSize="small" />,
      selected: path.includes("searchattempts"),
      onClick: props.searchAttempts,
    },
    hasTab(authState, "pending-audits") && {
      key: "pending-audits",
      label: "Pending Audits",
      icon: <RateReviewIcon fontSize="small" />,
      selected: path.includes("pending-audits"),
      onClick: props.pendingAudits,
    },
    hasTab(authState, "approved-audits") && {
      key: "approved-audits",
      label: "Approved Audits",
      icon: <ThumbUpIcon fontSize="small" />,
      selected: path.includes("approved-audits"),
      onClick: props.approvedAudits,
    },
    hasTab(authState, "rejected-audits") && {
      key: "rejected-audits",
      label: "Rejected Audits",
      icon: <CancelOutlinedIcon fontSize="small" />,
      selected: path.includes("rejected-audits"),
      onClick: props.rejectedAudits,
    },
    hasTab(authState, "system-setting") && {
      key: "reservation-sync",
      label: "Reservation Sync",
      icon: <SyncIcon fontSize="small" />,
      selected: path.includes("reservation-sync"),
      onClick: props.reservationSync,
    },
    hasTab(authState, "system-setting") && {
      key: "lease-documents",
      label: "Lease Documents",
      icon: <DescriptionIcon fontSize="small" />,
      selected: path.includes("lease-documents"),
      onClick: props.leaseDocuments,
    },
  ].filter(Boolean);

  const adminItems = [
    hasTab(authState, "agent") && {
      key: "agent",
      label: "Agents",
      icon: <WorkOutlineIcon fontSize="small" />,
      selected: path.includes("/agent"),
      onClick: props.agents,
    },
    hasTab(authState, "users") &&
      !authState.editAccess && {
        key: "users",
        label: "Users",
        icon: <PeopleAltIcon fontSize="small" />,
        selected: path.includes("users"),
        onClick: props.users,
      },
    {
      key: "consent-data",
      label: "Consent Data",
      icon: <AssignmentIcon fontSize="small" />,
      selected: path.includes("consent-data"),
      onClick: props.consentData,
    },
    hasTab(authState, "system-setting") && {
      key: "system-setting",
      label: "System Settings",
      icon: <SettingsIcon fontSize="small" />,
      selected: path.includes("system-setting"),
      onClick: props.systemSettings,
    },
    hasTab(authState, "setting") && {
      key: "settings",
      label: "Account Settings",
      icon: <SettingsIcon fontSize="small" />,
      selected: path.includes("/settings") || path.endsWith("settings"),
      onClick: props.settings,
    },
  ].filter(Boolean);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <List dense disablePadding sx={{ flex: 1, overflowY: "auto", py: 1 }}>
        <NavItem
          open={open}
          selected={path.includes("overview") || path.endsWith("/dashboard")}
          icon={<DashboardOutlinedIcon fontSize="small" />}
          label="Overview"
          onClick={props.overview}
        />

        {inventoryItems.length > 0 && (
          <>
            <SectionHeader open={open} label="Inventory" />
            {inventoryItems.map((item) => (
              <NavItem key={item.key} open={open} {...item} />
            ))}
          </>
        )}

        {commerceItems.length > 0 && (
          <>
            <SectionHeader open={open} label="Commerce" />
            {commerceItems.map((item) => (
              <NavItem key={item.key} open={open} {...item} />
            ))}
          </>
        )}

        {operationsItems.length > 0 && (
          <>
            <SectionHeader open={open} label="Operations" />
            {operationsItems.map((item) => (
              <NavItem key={item.key} open={open} {...item} />
            ))}
          </>
        )}

        {adminItems.length > 0 && (
          <>
            <SectionHeader open={open} label="Admin" />
            {adminItems.map((item) => (
              <NavItem key={item.key} open={open} {...item} />
            ))}
          </>
        )}
      </List>

      <Box sx={{ mt: "auto", pb: 1 }}>
        <Divider sx={{ mb: 1 }} />
        {open && authState?.name && (
          <Box sx={{ px: 2.5, pb: 1 }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {authState.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {authState.role || "User"}
            </Typography>
          </Box>
        )}
        <NavItem
          open={open}
          selected={false}
          icon={<ExitToAppIcon fontSize="small" />}
          label="Logout"
          onClick={props.logout}
        />
      </Box>
    </Box>
  );
}
